import { FlowRoute } from '@eneco/flows/types';

import { FlowConsumerContextEnergy } from '../../context';
import { TestConfigVariant, MergedConfig, RouteInjection } from '../types/experiment';

/**
 * Injects a route at a specific position in the routes array.
 * @param routes Current routes array
 * @param injection Route injection config with positioning
 * @returns New routes array with injected route
 */
const injectRoute = (
  routes: FlowRoute<FlowConsumerContextEnergy>[],
  injection: RouteInjection,
): FlowRoute<FlowConsumerContextEnergy>[] => {
  const { route, afterId, beforeId } = injection;
  const newRoutes = [...routes];

  if (afterId) {
    const index = newRoutes.findIndex(r => r.id === afterId);
    if (index !== -1) {
      newRoutes.splice(index + 1, 0, route);
      return newRoutes;
    }
  }

  if (beforeId) {
    const index = newRoutes.findIndex(r => r.id === beforeId);
    if (index !== -1) {
      newRoutes.splice(index, 0, route);
      return newRoutes;
    }
  }

  return newRoutes;
};

export const mergeConfigsFromArray = (
  defaultConfig: MergedConfig,
  activeVariants?: TestConfigVariant[],
): MergedConfig => {
  if (!activeVariants) return defaultConfig;
  const { routes: defaultRoutes, actions: defaultActions, initialContext: defaultInitialContext } = defaultConfig;

  let mergedRoutes = [...defaultRoutes];
  const mergedActions = { ...defaultActions };
  const mergedInitialContext = { ...defaultInitialContext };

  activeVariants.forEach(variant => {
    variant.routes?.forEach(replacement => {
      const index = mergedRoutes.findIndex(r => r.id === replacement.id);
      if (index !== -1) mergedRoutes[index] = replacement;
    });
    variant.injectRoutes?.forEach(injection => {
      mergedRoutes = injectRoute(mergedRoutes, injection);
    });
    if (variant.removeRouteIds?.length) {
      mergedRoutes = mergedRoutes.filter(route => !variant.removeRouteIds?.includes(route.id));
    }

    Object.assign(mergedActions, variant.actions);
    Object.assign(mergedInitialContext, variant.initialContext);
  });

  return {
    routes: mergedRoutes,
    actions: mergedActions,
    initialContext: mergedInitialContext,
  };
};
